import { Link, useLocation } from 'react-router-dom';

import type { PrivateEventLifecycle } from '../../api/privateEvents';
import { workshopShellFromPathname } from '../dashboards/workshopDetailUrlFor';

import { privateEventBackTarget } from './private-event-return';

/** Pill colour per lifecycle — mirrors the tones the rest of dashboards uses
 *  for "good / done / stopped" without inventing a fourth vocabulary. */
const LIFECYCLE_COLOR: Record<PrivateEventLifecycle, string> = {
  CONFIRMED: 'var(--nb-primary)',
  COMPLETED: 'var(--nb-subtle)',
  CANCELLED: 'var(--nb-risk)',
};

const LIFECYCLE_LABEL: Record<PrivateEventLifecycle, string> = {
  CONFIRMED: 'Confirmed',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

/**
 * PrivateEventDetailHeader — the detail page's header band. Same full-bleed
 * shape as `PrivateEventsPageHeader`, with a back link to wherever the event
 * was opened from (the list when that is unknown) + the client's name.
 *
 * `event` is null while loading or on a failed load, so the crumb still reads
 * sensibly before the record arrives.
 */
export default function PrivateEventDetailHeader({
  clientName,
  lifecycle,
}: {
  clientName: string | null;
  lifecycle?: PrivateEventLifecycle;
}) {
  const location = useLocation();
  const back = privateEventBackTarget(location.state, workshopShellFromPathname(location.pathname));
  return (
    <header
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 24,
        padding: '22px 32px 14px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="nb-eyebrow" style={{ marginBottom: 4, color: 'var(--nb-muted)' }}>
          <Link to={back.pathname} style={{ color: 'inherit', textDecoration: 'none' }}>
            ‹ Back to {back.label}
          </Link>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h1
            className="nb-display"
            style={{ margin: 0, fontSize: 30, color: 'var(--nb-ink)', letterSpacing: '0.01em' }}
          >
            {clientName ?? 'Private event'}
          </h1>
          {lifecycle && (
            <span
              className="nb-pill"
              style={{ fontWeight: 700, fontSize: 11, color: LIFECYCLE_COLOR[lifecycle] }}
            >
              {LIFECYCLE_LABEL[lifecycle]}
            </span>
          )}
        </div>
      </div>
    </header>
  );
}
